/**
 * @module routes.Sessions.hooks.useCoachOrgProfile
 */
import { useEffect, useState } from "react";

import { fetchGet } from "app/utils/request";
import settings from "config/environment";

export interface CoachOrgProfile {
  about?: string;
  awards?: string;
  phone?: string;
  email?: string;
  website?: string;
  coverUrl?: string;
  /**
   * The coach's Booking Service Link, from their own profile settings — set when
   * they take bookings at all, and an address elsewhere when they take them
   * somewhere other than CoachNow. Empty for a coach who has never set one up,
   * which is what decides whether the page offers to book with them.
   */
  bookingServiceLink?: string;
  address?: string;
  city?: string;
  country?: string;
  zip?: string;
}

/**
 * The coach's own profile from the core API — bio, awards, contact details and
 * cover photo.
 *
 * The scheduling API's `landing_page_coach` has none of that: it answers with
 * booking data, and of the fields here only `email`.
 *
 * **Two endpoints, and which one matters.** `/api/public/coach_profiles/:id` —
 * what the public coach landing page reads — answers only for a coach who has
 * published a landing page. For anyone else it is `{"error":"not_found"}`, which
 * left a signed-in athlete looking at a coach they share four Spaces with and
 * seeing no bio, no phone and no website, while that coach's own settings page
 * says "Your athletes can contact you through these contact details".
 *
 * `/api/v2/coach_profiles/:id` is the same record without that gate, and it is
 * what the app's own coach profile page (`CoachProfileInfo`) has always read for
 * exactly these fields. So a signed-in viewer asks that one, and the public
 * endpoint stays the fallback — for a visitor, and for a coach the viewer turns
 * out not to be allowed to read directly. The public coach landing page fetches both and merges them, and
 * this is the same second request, so the in-app page can show the same page.
 *
 * A failure is not an error state: the page still has everything bookable, so it
 * renders without a bio rather than not at all.
 */
const useCoachOrgProfile = (
  coachExternalId: string | undefined,
  /**
   * Whether the viewer is signed in, which decides which endpoint is asked
   * first. A visitor has only the public one, and asking the authenticated one
   * on their behalf buys a 401 per coach page.
   */
  signedIn = false,
): { orgProfile: CoachOrgProfile | null; loading: boolean } => {
  const [orgProfile, setOrgProfile] = useState<CoachOrgProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!coachExternalId) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);

    /** The published-landing-page copy, which anyone may read. */
    const publicProfile = () =>
      fetchGet(
        `coach_profiles/${coachExternalId}`,
        { clientId: settings.API_CLIENT_ID },
        {},
        undefined,
        true,
      );

    /** The same record for a signed-in viewer, published or not. */
    const authenticatedProfile = () =>
      fetchGet(`coach_profiles/${coachExternalId}`);

    (signedIn
      ? authenticatedProfile().catch(() => publicProfile())
      : publicProfile()
    )
      .then((response: any) => {
        if (active) {
          setOrgProfile(response || null);
        }
      })
      .catch(() => {
        if (active) {
          setOrgProfile(null);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [coachExternalId, signedIn]);

  return { orgProfile, loading };
};

export default useCoachOrgProfile;
